import React from "react";
import { hashHistory } from "react-router";
import Grid from "@material-ui/core/Grid";
import TableInc from "../../../shared-ui/TableInc";
import ButtonInc from "../../../shared-ui/ButtonInc";
import { formatDateLocale } from "../../../lib/utils.es6";
import { getPartner } from "../../../lib/access.es6";

const DisplayRecentPayments = (props) => {
  const partner = getPartner();
  const currencyCode =
    partner && partner.country && partner.country.currency_code
      ? partner.country.currency_code
      : "";

  // Process payments data for table display
  const paymentsData = props.payments || [];
  console.log('Recent Payments - Data:', paymentsData, 'Total:', props.total, 'Page:', props.page);
  
  const tempArr = paymentsData
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map((payment) => ({
      ...payment,
      created_at: formatDateLocale(payment.created_at) || "N/A",
      effected_at: formatDateLocale(payment.effected_at) || "N/A",
      amount_display: payment.amount_in_cents
        ? `${currencyCode} ${(payment.amount_in_cents / 100).toLocaleString()}`
        : "N/A",
      processing_status_display: payment.processing_status || "N/A",
      status_display: payment.status || "N/A",
      mno_reference_display: payment.mno_reference || "N/A",
      payment_failure_reason_display: payment.payment_failure_reason || "N/A",
      msisdn_display: payment.msisdn || "N/A",
    }));

  // Define table columns
  const columns = [
    {
      title: "MNO Reference",
      field: "mno_reference_display",
      render: (rowData) => (
        <span style={{ fontFamily: 'monospace', color: '#2D3748', fontWeight: '500' }}>
          {rowData.mno_reference_display}
        </span>
      ),
    },
    {
      title: "MSISDN",
      field: "msisdn_display",
      render: (rowData) => (
        <span style={{ fontFamily: 'monospace', color: '#2D3748' }}>
          {rowData.msisdn_display}
        </span>
      ),
    },
    {
      title: "Amount",
      field: "amount_display",
      render: (rowData) => (
        <span style={{ fontWeight: '600', color: '#2D3748' }}>
          {rowData.amount_display}
        </span>
      ),
    },
    {
      title: "Processing Status",
      field: "processing_status_display",
      render: (rowData) => {
        const status = rowData.processing_status;
        let statusColor = '#666666';
        let backgroundColor = '#f3f4f6';
        
        switch (status) {
          case 'Succeeded':
            statusColor = '#166534';
            backgroundColor = '#dcfce7';
            break;
          case 'Processing':
            statusColor = '#c2410c';
            backgroundColor = '#fed7aa';
            break;
          case 'Failed':
            statusColor = '#dc2626';
            backgroundColor = '#fecaca';
            break;
          case 'Unconfirmed':
            statusColor = '#92400e';
            backgroundColor = '#fef3c7';
            break;
          case 'Received':
            statusColor = '#1e40af';
            backgroundColor = '#dbeafe';
            break;
          case 'Rejected':
            statusColor = '#6b7280';
            backgroundColor = '#f3f4f6';
            break;
          default:
            statusColor = '#666666';
            backgroundColor = '#f3f4f6';
            break;
        }
        
        return (
          <span style={{ 
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
            color: statusColor,
            backgroundColor: backgroundColor
          }}>
            {rowData.processing_status_display}
          </span>
        );
      },
    },
    {
      title: "Status",
      field: "status_display",
      render: (rowData) => {
        const status = rowData.status;
        let statusColor = '#666666';
        let backgroundColor = '#f3f4f6';
        
        switch (status) {
          case 'Succeeded':
            statusColor = '#166534';
            backgroundColor = '#dcfce7';
            break;
          case 'Failed':
            statusColor = '#dc2626';
            backgroundColor = '#fecaca';
            break;
          case 'Unknown':
            statusColor = '#92400e';
            backgroundColor = '#fef3c7';
            break;
          default:
            statusColor = '#666666';
            backgroundColor = '#f3f4f6';
            break;
        }
        
        return (
          <span style={{ 
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
            color: statusColor,
            backgroundColor: backgroundColor
          }}>
            {rowData.status_display}
          </span>
        );
      },
    },
    {
      title: "Payment Failure Reason",
      field: "payment_failure_reason_display",
      render: (rowData) => {
        const reason = rowData.payment_failure_reason_display;
        if (reason === "N/A" || !reason) {
          return <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>N/A</span>;
        }
        return (
          <span style={{ 
            color: '#DC2626', 
            fontSize: '12px',
            backgroundColor: '#FEF2F2',
            padding: '2px 6px',
            borderRadius: '4px'
          }}>
            {reason}
          </span>
        );
      },
    },
    {
      title: "Effected At",
      field: "effected_at",
      render: (rowData) => rowData.effected_at || "N/A",
    },
    {
      title: "Created Date",
      field: "created_at",
      render: (rowData) => rowData.created_at || "N/A",
    },
    {
      title: "Actions",
      field: "actions",
      render: (rowData) => {
        return (
          <div>
            <ButtonInc
              onClick={() => {
                // Navigate to payment details or customer details
                if (rowData.customer_guid) {
                  hashHistory.push(`admin/customer_status/${rowData.customer_guid}`);
                }
              }}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              View Customer
            </ButtonInc>
          </div>
        );
      },
    },
  ];

  // Both footer controls land here: material-table reports a rows-per-page change
  // as onChangePage(0, newSize), so there is nothing to add for the rows-per-page
  // select. Its onChangeRowsPerPage is deliberately NOT wired — that one is called
  // with the size alone, so reusing this handler would read the size as the page
  // number and ask the server for page 21.
  const handleTableChange = (newPage, pageSize) => {
    const payload = {
      page: newPage + 1,
      size: pageSize,
    };
    props.handleNextPage(payload);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12} sm={12}>
        <TableInc
            columns={columns}
            data={tempArr}
            // page / totalCount / onChangePage / localization are material-table's
            // own props, NOT options — nothing reads them off the options object, so
            // while they sat in there every one of them was inert: the footer
            // counted the twenty rows it had been handed and reported "1-20 of 20"
            // over a result set of 55, next was disabled before it could be
            // clicked, and handleNextPage was never called. The pair has to travel
            // together for material-table to trust the server count at all (its
            // isOutsidePageNumbers check wants both).
            page={(props.page || 1) - 1}
            totalCount={props.total || 0}
            onChangePage={handleTableChange}
            localization={{
              body: {
                emptyDataSourceMessage:
                  'No payments found for the selected criteria'
              }
            }}
            options={{
              pageSize: props.size || 20,
              paging: true,
              showTitle: false,
              // material-table's search only filters the rows it is holding — one
              // server page. With the server total now honoured it would count all
              // 55 matches while showing only this page's, and leave next enabled
              // over an unrelated set. There is no search parameter on the recent
              // payments endpoint to push it to, so it stays off.
              search: false,
              emptyRowsWhenPaging: false,
              showEmptyDataSourceMessage: true
            }}
          />
        </Grid>
      </Grid>
  );
};

export default DisplayRecentPayments;